import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getProperties, createProperty, updateProperty, deleteProperty } from '../api/properties-api';
import type { PropertySortBy, PropertySortDir } from '../api/properties-api';
import { useDataCache, makePaginatedCacheKey } from '@/store/data-cache-context';
import { useOrg } from '@/store/org-context';
import { useDebounce } from '@/lib/utils';
import { usePagePolling, POLL_INTERVAL_LIST_MS } from '@/lib/use-page-polling';
import type { Property, PaginatedResponse, CreatePropertyRequest } from '@/types/api';
import { bulkCreateBuildings } from '@/features/buildings/api/buildings-api';
import type { PropertyModalBulkCreatePayload, PropertyModalBulkCreateResult } from '../components/property-modal';

const PER_PAGE = 15;

// May 15 batch: server-side sort whitelist for the new /properties list
// view. Matches the PropertyRepository.GetPagedAggregatesAsync values;
// a stale client choice falls back to name asc server-side.
const SORT_BY_VALUES: PropertySortBy[] = [
  'name',
  'buildingCount',
  'unitCount',
  'deviceCount',
  'activeAlarmCount',
  'createdAt',
];

function parseSortBy(raw: string | null): PropertySortBy {
  return (SORT_BY_VALUES as string[]).includes(raw ?? '')
    ? (raw as PropertySortBy)
    : 'name';
}

function parseSortDir(raw: string | null): PropertySortDir {
  return raw === 'desc' ? 'desc' : 'asc';
}

export function usePropertiesPage() {
  const { getPropertiesPage, setPropertiesPage, invalidate } = useDataCache();
  const { activeOrgId } = useOrg();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const propertiesPage = Number(searchParams.get('page') || '1');
  const sortBy = parseSortBy(searchParams.get('sortBy'));
  const sortDir = parseSortDir(searchParams.get('sortDir'));

  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const debouncedSearch = useDebounce(searchInput, 300);

  const [propertiesResult, setPropertiesResult] = useState<PaginatedResponse<Property> | null>(null);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [deletingProperty, setDeletingProperty] = useState<Property | null>(null);
  const [isDeletingProperty, setIsDeletingProperty] = useState(false);

  const properties = propertiesResult?.items ?? [];
  const propertiesTotalPages = propertiesResult?.totalPages ?? 1;
  const propertiesTotalCount = propertiesResult?.totalCount ?? 0;

  // URL sync: search
  useEffect(() => {
    if (debouncedSearch === (searchParams.get('q') || '')) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch) next.set('q', debouncedSearch);
        else next.delete('q');
        next.delete('page');
        return next;
      },
      { replace: true }
    );
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load properties
  useEffect(() => {
    loadProperties();
  }, [debouncedSearch, propertiesPage, sortBy, sortDir, activeOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Background poll: visibility-gated list-page cadence. forceRefresh:true
  // bypasses the page-keyed cache so the list reflects mutations from
  // other tabs without waiting on cache TTL.
  usePagePolling(() => { void loadProperties(true); }, POLL_INTERVAL_LIST_MS);

  async function loadProperties(forceRefresh = false) {
    const cacheKey = makePaginatedCacheKey({
      search: debouncedSearch,
      page: propertiesPage,
      perPage: PER_PAGE,
      sortBy,
      sortDir,
    });
    if (!forceRefresh) {
      const cached = getPropertiesPage(cacheKey);
      if (cached) {
        setPropertiesResult(cached);
        setPropertiesLoading(false);
        return;
      }
    }
    // Only show the full-page loading state for the very first fetch.
    // Subsequent calls (search typing, page change, polling) keep the
    // existing list rendered so the search input doesn't unmount
    // mid-keystroke and lose focus.
    if (!forceRefresh && !propertiesResult) setPropertiesLoading(true);
    try {
      const response = await getProperties({
        search: debouncedSearch || undefined,
        page: propertiesPage,
        perPage: PER_PAGE,
        sortBy,
        sortDir,
      });
      setPropertiesPage(cacheKey, response);
      setPropertiesResult(response);
    } catch (error) {
      console.error('Failed to load properties:', error);
    } finally {
      setPropertiesLoading(false);
    }
  }

  // Toggle on a column header / sort toolbar: clicking the active sort
  // flips direction; otherwise resets to ascending. Sort changes always
  // jump back to page 1 so the user lands at the new top of the list.
  const handleSortChange = useCallback((nextSortBy: PropertySortBy) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const currentBy = parseSortBy(prev.get('sortBy'));
        const currentDir = parseSortDir(prev.get('sortDir'));
        const flipping = currentBy === nextSortBy;
        const nextDir: PropertySortDir = flipping ? (currentDir === 'asc' ? 'desc' : 'asc') : 'asc';
        if (nextSortBy === 'name' && nextDir === 'asc') {
          next.delete('sortBy');
          next.delete('sortDir');
        } else {
          next.set('sortBy', nextSortBy);
          next.set('sortDir', nextDir);
        }
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleSortDirChange = useCallback((nextDir: PropertySortDir) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const currentBy = parseSortBy(prev.get('sortBy'));
        if (currentBy === 'name' && nextDir === 'asc') {
          next.delete('sortBy');
          next.delete('sortDir');
        } else {
          next.set('sortBy', currentBy);
          next.set('sortDir', nextDir);
        }
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  function handlePropertiesPageChange(page: number) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page > 1) next.set('page', String(page));
        else next.delete('page');
        return next;
      },
      { replace: true }
    );
  }

  function handleAddProperty() {
    setEditingProperty(null);
    setIsPropertyModalOpen(true);
  }

  function handleEditProperty(property: Property, e: React.MouseEvent) {
    e.stopPropagation();
    setEditingProperty(property);
    setIsPropertyModalOpen(true);
  }

  async function handleDeletePropertyConfirm() {
    if (!deletingProperty) return;
    setIsDeletingProperty(true);
    const prevResult = propertiesResult;
    if (propertiesResult) {
      setPropertiesResult({
        ...propertiesResult,
        items: propertiesResult.items.filter(p => p.id !== deletingProperty.id),
        totalCount: propertiesResult.totalCount - 1,
      });
    }
    setDeletingProperty(null);
    try {
      await deleteProperty(deletingProperty.id);
      invalidate(['properties']);
    } catch (error) {
      setPropertiesResult(prevResult);
      console.error('Failed to delete property:', error);
    } finally {
      setIsDeletingProperty(false);
    }
  }

  async function handleSaveProperty(data: CreatePropertyRequest) {
    if (editingProperty) {
      const prevResult = propertiesResult;
      if (propertiesResult) {
        const optimistic = { ...editingProperty, ...data, updatedAt: new Date().toISOString() };
        setPropertiesResult({
          ...propertiesResult,
          items: propertiesResult.items.map(p => p.id === editingProperty.id ? optimistic : p),
        });
      }
      setIsPropertyModalOpen(false);
      try {
        await updateProperty(editingProperty.id, data);
        invalidate(['properties']);
      } catch (error) {
        setPropertiesResult(prevResult);
        console.error('Failed to update property:', error);
      }
    } else {
      const response = await createProperty(data);
      setIsPropertyModalOpen(false);
      if (response.data) {
        invalidate(['properties']);
        navigate(`/properties/${response.data.id}`);
      }
    }
  }

  // Two-step create: property basics → buildings/units bulk-create. Used
  // by PropertyModal when the user takes the Step 2 path. On bulk failure
  // (422), the property exists but Step 2 stays open with row errors so
  // the user can fix and retry without recreating the property.
  async function handleBulkCreateProperty(
    payload: PropertyModalBulkCreatePayload,
  ): Promise<PropertyModalBulkCreateResult> {
    const response = await createProperty(payload.propertyBasics);
    if (!response.data) {
      throw new Error('Failed to create property.');
    }
    const propertyId = response.data.id;
    if (payload.buildings.length === 0) {
      invalidate(['properties']);
      navigate(`/properties/${propertyId}`);
      return { propertyId, bulk: { ok: true } };
    }
    const bulk = await bulkCreateBuildings({ propertyId, buildings: payload.buildings });
    if (bulk.ok) {
      invalidate(['properties', 'buildings', 'units']);
      navigate(`/properties/${propertyId}`);
    }
    return { propertyId, bulk };
  }

  return {
    properties,
    propertiesLoading,
    propertiesTotalPages,
    propertiesTotalCount,
    propertiesPage,
    sortBy,
    sortDir,
    handleSortChange,
    handleSortDirChange,
    searchInput,
    setSearchInput,
    handlePropertiesPageChange,
    editingProperty,
    isPropertyModalOpen,
    setIsPropertyModalOpen,
    deletingProperty,
    isDeletingProperty,
    setDeletingProperty,
    handleAddProperty,
    handleEditProperty,
    handleDeletePropertyConfirm,
    handleSaveProperty,
    handleBulkCreateProperty,
  };
}

export type PropertiesPageState = ReturnType<typeof usePropertiesPage>;
